"use client"
import React, { useEffect, useMemo, useState } from "react"
import Image from "next/image"
import styles from "./HorizontalCollapsibleRow.module.scss"
import StyledText from "src/typography/typography-component"
import skColors from "styles/skColors"
import Zoom from "@mui/material/Zoom"

export type Person = {
  key: string
  name: string
  title?: string
  bullets?: string[]
  img: string
  linkedin?: string
}

function shuffleOnce<T>(arr: T[]) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

export default function InlineExpandStrip({
  people,
  shuffle = false,
  variant = "team",
}: {
  people: Person[]
  shuffle?: boolean
  variant?: "team" | "board"
}) {
  const [openKey, setOpenKey] = useState<string | null>(null)
  const [list, setList] = useState<Person[]>([])
  const [detectiveActive, setDetectiveActive] = useState(false)
  const detectiveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    const _list = shuffle ? shuffleOnce(people) : people
    setList(_list)
  }, [])

  return (
    <div className={`${styles.row} ${styles[variant]}`} role="list">
      {list.map((p) => {
        const open = openKey === p.key
        return (
          <button
            key={p.key}
            role="listitem"
            className={`${styles.card} ${open ? styles.open : ""} ${p.key === "Nils" && detectiveActive ? styles.detective : ""}`}
            onClick={() => setOpenKey(open ? null : p.key)}
            aria-expanded={open}
            onMouseEnter={() => {
              if (p.key === "Nils" || p.key === "till") {
                detectiveTimer.current = setTimeout(() => setDetectiveActive(true), 35000)
              }
            }}
            onMouseLeave={() => {
              if (p.key === "Nils" || p.key === "till") {
                if (detectiveTimer.current) clearTimeout(detectiveTimer.current)
                setDetectiveActive(false)
              }
            }}
          >
            {/* Portrait */}
            <span className={`${styles.portrait}`}>
              <Image
                src={p.img}
                alt={p.name}
                fill
                sizes="96px"
                style={{ objectFit: "cover", objectPosition: "center top" }}
              />
            </span>
            {/* Expanding content */}
            <span className={styles.content}>
              <a href={p?.linkedin} target="_blank" rel="noopener noreferrer">
                <StyledText
                  variant="pSmall"
                  className={`${styles.title} ${styles.link}`}
                  color={skColors.darkgreen[600]}
                >
                  {p.name}
                </StyledText>
              </a>

              {p.title && (
                <StyledText variant="pSmall" className={styles.role} color={skColors.darkgreen[600]}>
                  {p.title}
                </StyledText>
              )}

              {p.bullets && p.bullets.length > 0 && (
                <ul className={`${styles.bullets} ${styles.naturalLanguage}`}>
                  {p.bullets.map((b, i) => (
                    <li lang={"de"} key={i}>
                      {b}
                    </li>
                  ))}
                </ul>
              )}
            </span>
          </button>
        )
      })}
    </div>
  )
}
